import styled from '@emotion/styled';
import { BucketId, FileReference } from '@kaizenplatform/belfast-entity';
import { Divider as _Divider, Paper, Typography } from '@material-ui/core';
import { DateTime } from 'luxon';
import { ClassAttributes, createElement, ReactElement } from 'react';
import { FormattedMessage } from 'react-intl';

import { ProjectDetailsForShowFragment } from '../../../generated/graphql';
import { toJSDate } from '../../../graphql/datetime';
import { ProjectForBrief } from '../../../graphql/project';
import { ProjectChangeType } from '../../../service/persistent/ProjectChangeWebApi';
import CreativeBriefDetailDiff from '../../component/compound/CreativeBriefDetailDiff';
import DummyCreativeBriefDetail from '../../component/compound/DummyCreativeBriefDetail';
import ProjectBasicSettingDetailDiff from '../../component/compound/ProjectBasicSettingDetailDiff';
import { SPACING_UNIT } from '../../constant/spacing';
import { gridLayoutContainer, gridLayoutItem } from '../../styleUtility/gridLayout';

type Props = ClassAttributes<HTMLElement> & {
  project: Pick<
    ProjectDetailsForShowFragment & ProjectForBrief,
    'creativeBriefDefinition' | 'assetCategoryDefinition' | 'creativeBriefValue'
  > | null;
  projectChange: ProjectChangeType | null;
  latestBriefFiles: Map<BucketId, FileReference[]>;
  beforeBriefFiles: Map<BucketId, FileReference[]>;
  assetFiles: ReadonlyArray<FileReference>;
  briefFiles: Map<BucketId, FileReference[]>;
  loading: boolean;
  className?: string;
};

const CreativeBrief = ({
  project,
  projectChange,
  latestBriefFiles,
  beforeBriefFiles,
  assetFiles,
  briefFiles,
  loading,
  className,
}: Props) => {
  let brief: ReactElement<any>;
  let basicSetting: ReactElement<any> | null = null;

  if (
    loading ||
    !project ||
    !projectChange ||
    !projectChange.latestVersion.creativeBriefValues ||
    !projectChange.beforeVersion.creativeBriefValues
  ) {
    brief = <DummyCreativeBriefDetail />;
  } else {
    brief = (
      <CreativeBriefDetailDiff
        definition={project.creativeBriefDefinition}
        value={project.creativeBriefValue}
        latestValue={projectChange.latestVersion.creativeBriefValues}
        beforeValue={projectChange.beforeVersion.creativeBriefValues}
        files={briefFiles}
        latestFiles={latestBriefFiles}
        beforeFiles={beforeBriefFiles}
      />
    );
    if (project != null && projectChange.latestVersion.assetCategoryValues != null) {
      basicSetting = (
        // asset表示？ ここも対応する必要あるかも
        <ProjectBasicSettingDetailDiff
          definition={project.creativeBriefDefinition}
          assetFiles={assetFiles}
          latestAssetValue={projectChange.latestVersion.assetCategoryValues}
          beforeAssetValue={projectChange.beforeVersion.assetCategoryValues}
          assetCategorySections={
            project.assetCategoryDefinition != null ? project.assetCategoryDefinition.sections : null
          }
        />
      );
    }
  }

  return (
    <Root className={className}>
      <Heading variant="h5">
        <FormattedMessage id="project_detail_page.creative_brief.heading" />
        {projectChange ? (
          <UpdateTitle>
            <FormattedMessage id="project_detail_page.creative_brief.updated_at" />:{' '}
            {DateTime.fromJSDate(toJSDate(projectChange.updatedAt)).toFormat('yyyy-MM-dd HH:mm:ss')}
          </UpdateTitle>
        ) : null}
      </Heading>
      <Content>
        {basicSetting}
        <Divider />
        {brief}
      </Content>
    </Root>
  );
};

const Root = styled('div')({
  ...gridLayoutContainer({
    columns: ['100%'],
    rows: ['auto', 'auto'],
    areas: [['heading'], ['content']],
  }),
});

const Heading = styled(Typography)({
  ...gridLayoutItem('heading'),
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: SPACING_UNIT * 3,
});

const UpdateTitle = styled('div')({
  fontSize: '18px',
});
const Content = styled(Paper)({
  ...gridLayoutItem('content'),
  padding: `${SPACING_UNIT * 3}px ${SPACING_UNIT * 2}px`,
});

const Divider = styled(_Divider)({
  margin: `${SPACING_UNIT * 3}px 0`,
});

export default CreativeBrief;
